import { Fragment } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import StakingItemCountDown from './StakingItemCountDown'

export default function StakingScheduleDialog({ program, onClose }) {
  return (
    <Transition.Root show={true} as={Fragment}>
      <Dialog as="div" className="relative z-10" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-opacity-95 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 w-screen overflow-y-auto bg-jacarta-200 bg-opacity-60">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="w-full h-full max-w-md transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                <Dialog.Title as="h3" className="text-lg font-bold leading-6 text-jacarta-800">
                  Staking Schedule{' '}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    strokeWidth={1.5}
                    stroke="currentColor"
                    className="w-6 inline-block"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25"
                    />
                  </svg>
                </Dialog.Title>
                <div className="relative mt-6 h-[335px] overflow-scroll pb-3">
                  <div className="flow-root">
                    <ul className="-mb-8">
                      <li>
                        <div className="relative pb-8">
                          <div className="relative">
                            <div className="flex items-center justify-start p-3">
                              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-400 ring-8 ring-jacarta-100">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className={`h-4 w-4 ${
                                    program.status === 'READY' ? 'text-green' : 'text-jacarta-700'
                                  }`}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                                  />
                                </svg>
                              </span>
                              <span
                                className={`pl-6 font-semibold text-lg ${
                                  program.status === 'READY' ? 'text-green' : 'text-jacarta-700'
                                }`}
                              >
                                Ready
                              </span>
                              {program.status === 'READY' && (
                                <div className="pl-3 text-[12px]">
                                  <span className="rounded-full border bg-green bg-opacity-5 text-green pl-2 pr-2 pt-1 pb-1">
                                    On Progress
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="mt-3">
                            <div>
                              <p className="mt-0.5 text-[12px] text-jacarta-500">
                                This is a reward pool that staking <b>${program.depositToken}</b> to earn a certain
                                amount of <b>${program.rewardToken}</b>. WalkMiner can earn{' '}
                                <b>
                                  {program.rewardCycle === 'DAY' && 'daily'} {program.rewardCycle === 'WEEK' && 'week'}{' '}
                                  {program.rewardCycle === 'MONTH' && 'month'}
                                </b>{' '}
                                rewards but cannot withdraw during the staking period.
                                <br />
                                For example, WalkMiner gets a reward of a day before the day.
                              </p>
                            </div>
                            {program.status === 'READY' && (
                              <div className="items-center text-center text-[20px] text-jacarta-500 mt-4">
                                <span className="text-[12px] text-jacarta-500">Start in</span>
                                <div className="flex items-center justify-center">
                                  <StakingItemCountDown time={program.countDownMillisecond} gradient={false} />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="relative pb-8">
                          <div className="relative">
                            <div className="flex items-center justify-start p-3">
                              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-400 ring-8 ring-jacarta-100">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className={`h-4 w-4 ${
                                    program.status === 'IN_PROGRESS_DEPOSIT' ? 'text-green' : 'text-jacarta-700'
                                  }`}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5"
                                  />
                                </svg>
                              </span>
                              <div className="flex items-center justify-center">
                                <span
                                  className={`pl-6 font-semibold text-lg ${
                                    program.status === 'IN_PROGRESS_DEPOSIT' ? 'text-green' : 'text-jacarta-700'
                                  }`}
                                >
                                  Deposit
                                </span>
                                {program.status === 'IN_PROGRESS_DEPOSIT' && (
                                  <div className="pl-3 text-[12px]">
                                    <span className="rounded-full border bg-green bg-opacity-5 text-green pl-2 pr-2 pt-1 pb-1">
                                      On Progress
                                    </span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                          <div className="mt-3">
                            <div>
                              <p className="mt-0.5 text-[12px] font-bold text-jacarta-500">
                                {program.depositStartDateTime} ~ {program.depositEndDateTime}(UTC)
                              </p>
                            </div>
                            <div className="text-[13px] text-jacarta-500">
                              <p>
                                A deposit of ${program.depositToken} on the reward pool is available during the above
                                period.
                              </p>
                            </div>
                            {program.status === 'IN_PROGRESS_DEPOSIT' && (
                              <div className="items-center text-center text-[20px] text-jacarta-500 mt-4">
                                <span className="text-[12px] text-jacarta-500">Deposit Ended in</span>
                                <div className="flex items-center justify-center">
                                  <StakingItemCountDown time={program.countDownMillisecond} gradient={false} />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="relative pb-8">
                          <div className="relative">
                            <div className="flex items-center justify-start p-3">
                              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-400 ring-8 ring-jacarta-100">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className={`h-4 w-4 ${
                                    program.status === 'IN_PROGRESS_STAKING' ? 'text-green' : 'text-jacarta-700'
                                  }`}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z"
                                  />
                                </svg>
                              </span>
                              <span
                                className={`pl-6 font-semibold text-lg ${
                                  program.status === 'IN_PROGRESS_STAKING' ? 'text-green' : 'text-jacarta-700'
                                }`}
                              >
                                Staking
                              </span>
                              {program.status === 'IN_PROGRESS_STAKING' && (
                                <div className="pl-3 text-[12px]">
                                  <span className="rounded-full border bg-green bg-opacity-5 text-green pl-2 pr-2 pt-1 pb-1">
                                    On Progress
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="mt-3">
                            <div>
                              <p className="mt-0.5 text-[12px] font-bold text-jacarta-500">
                                {program.stakingStartDateTime} ~ {program.stakingEndDateTime}(UTC)
                              </p>
                            </div>
                            <div className="text-[13px] text-jacarta-500">
                              <p>
                                Rewards are being accumulated. WalkMiner can get rewards according to the distribution
                                schedule.
                              </p>
                            </div>
                            {program.status === 'IN_PROGRESS_STAKING' && (
                              <div className="items-center text-center text-[20px] text-jacarta-500 mt-4">
                                <span className="text-[12px] text-jacarta-500">Staking Ended in</span>
                                <div className="flex items-center justify-center">
                                  <StakingItemCountDown time={program.countDownMillisecond} gradient={false} />
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </li>
                      <li>
                        <div className="relative pb-8">
                          <div className="relative">
                            <div className="flex items-center justify-start p-3">
                              <span className="flex h-7 w-7 items-center justify-center rounded-full bg-gray-400 ring-8 ring-jacarta-100">
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  strokeWidth={1.5}
                                  stroke="currentColor"
                                  className={`h-4 w-4 ${
                                    program.status === 'ENDED' ? 'text-green' : 'text-jacarta-700'
                                  }`}
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M21 11.25v8.25a1.5 1.5 0 01-1.5 1.5H5.25a1.5 1.5 0 01-1.5-1.5v-8.25M12 4.875A2.625 2.625 0 109.375 7.5H12m0-2.625V7.5m0-2.625A2.625 2.625 0 1114.625 7.5H12m0 0V21m-8.625-9.75h18c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125h-18c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z"
                                  />
                                </svg>
                              </span>
                              <span
                                className={`pl-6 font-semibold text-lg ${
                                  program.status === 'ENDED' ? 'text-green' : 'text-jacarta-700'
                                }`}
                              >
                                Ended
                              </span>
                              {program.status === 'ENDED' && (
                                <div className="pl-3 text-[12px]">
                                  <span className="rounded-full border bg-green bg-opacity-5 text-green pl-2 pr-2 pt-1 pb-1">
                                    Finsh
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="mt-3">
                            <div>
                              <p className="mt-0.5 text-[12px] font-bold text-jacarta-500">
                                {program.claimStartDateTime} ~ {program.claimEndDateTime}(UTC)
                              </p>
                            </div>
                            <div className="text-[13px] text-jacarta-500">
                              <p>In this step, WalkMiner can receive all accumulated rewards at once and withdraw.</p>
                            </div>
                          </div>
                        </div>
                      </li>
                    </ul>
                  </div>
                </div>
                <div className="mb-4">
                  <button
                    type="button"
                    className="flex justify-center items-center w-full h-12 hover:bg-jacarta-600 text-[16px] rounded-full bg-[#131740] text-white"
                    onClick={() => {
                      onClose()
                    }}
                  >
                    Confirm
                  </button>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
}
